import { moduleFieldsOf } from "@/lib/geo";
import { useState } from "react";
import { Trash2, Loader2, Play, AlertTriangle, CircleSlash, PenSquare, Square } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt } from "@/lib/catalog";
import { makeFrame } from "@/lib/geo";
import { edgeAzimuth } from "@/editor/drawing";
import { dist } from "@/editor/mercator";

import { tr } from "@/lib/i18n";
const STATUS = { incomplete: tr("Incompleto"), preliminary: tr("Preliminare"), calculated_pending_review: tr("Calcolato · da revisionare") };

// Direzione del drenaggio: l'utente sceglie dopo il disegno quale lato è la gronda
export const DrainagePicker = ({ roof, commit }) => {
  if ((roof.polygon || []).length < 3) return <p className="text-xs text-slate-400">{tr("Disegna prima il contorno del tetto.")}</p>;
  const pm = roof.polygon.map(makeFrame(roof.polygon, 0).toUV);
  const g = (Number(roof.gutter_edge) || 0) % pm.length;
  return (
    <div className="space-y-1" data-testid="editor-drainage">
      {pm.map((a, i) => {
        const az = edgeAzimuth(pm, i), L = dist(a, pm[(i + 1) % pm.length]);
        return (
          <button key={i} data-testid={`editor-drainage-${i}`} onClick={() => commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === roof.id ? { ...r, gutter_edge: i, azimuth: az, removed: [] } : r)) }))}
            className={`w-full h-8 px-2 rounded-md border text-xs font-tech flex justify-between items-center ${g === i ? "bg-[#0F52BA] text-white border-[#0F52BA]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            <span>{tr("Lato")} {i + 1} · {fmt(L, 2)} m</span><span>{fmt(az, 1)}°</span>
          </button>
        );
      })}
      <p className="text-[11px] text-slate-400" data-testid="editor-azimuth-readout">{tr("Azimut falda")} <b className="text-slate-700">{fmt(roof.azimuth, 1)}°</b> {tr("(Sud = 0°, Ovest = +90°), ricavato dalla gronda selezionata.")}</p>
    </div>
  );
};

const N = ({ label, unit, value, onChange, testid, placeholder }) => (
  <label className="block"><span className="text-[11px] text-slate-500">{label}</span>
    <div className="flex items-center border border-slate-200 rounded mt-0.5">
      <input type="number" step="0.05" value={value ?? ""} placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="w-full min-w-0 h-8 px-2 text-sm font-tech outline-none bg-transparent" />
      <span className="px-1.5 text-[11px] text-slate-400">{unit}</span>
    </div>
  </label>
);

const TYPE = { rect: tr("Rettangolo"), poly: tr("Poligono"), line: tr("Linea") };

export const ObstacleList = ({ roof, commit, update, ed }) => {
  const set = (o, patch) => commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === roof.id ? { ...r, obstacles: r.obstacles.map((x) => (x.id === o.id ? { ...x, ...patch } : x)), removed: [] } : r)) }));
  const obs = roof.obstacles || [];
  if (!obs.length) return <p className="text-xs text-slate-400">{tr("Strumenti a destra: ostacolo rettangolare, area esclusa poligonale, ostacolo lineare.")}</p>;
  return obs.map((o) => (
    <div key={o.id} className={`border rounded-md p-2 mb-2 ${ed.selId === o.id ? "border-amber-400 bg-amber-50/40" : "border-slate-200"}`} data-testid={`editor-obstacle-${o.id}`}>
      <div className="flex gap-1.5 items-center">
        <span className="text-[10px] font-tech uppercase text-slate-400 w-16">{TYPE[o.type || "rect"]}</span>
        <input value={o.name} onChange={(e) => update((prev) => ({ roofs: prev.roofs.map((r) => (r.id === roof.id ? { ...r, obstacles: r.obstacles.map((x) => (x.id === o.id ? { ...x, name: e.target.value } : x)) } : r)) }))}
          className="flex-1 min-w-0 px-2 h-8 text-sm border border-slate-200 rounded outline-none" />
        <button data-testid={`editor-obstacle-del-${o.id}`} onClick={() => commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === roof.id ? { ...r, obstacles: r.obstacles.filter((x) => x.id !== o.id), removed: [] } : r)) }))}
          className="w-8 h-8 grid place-items-center text-slate-400 hover:text-amber-700"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        {(o.type || "rect") === "rect" && <>
          <N label={tr("Larghezza")} unit="m" value={o.w} testid={`editor-obstacle-w-${o.id}`} onChange={(v) => set(o, { w: Number(v) || 0 })} />
          <N label={tr("Profondità")} unit="m" value={o.h} testid={`editor-obstacle-h-${o.id}`} onChange={(v) => set(o, { h: Number(v) || 0 })} />
        </>}
        <N label={tr("Distanza di rispetto")} unit="m" value={o.clearance} placeholder={tr("globale")} testid={`editor-obstacle-clr-${o.id}`} onChange={(v) => set(o, { clearance: v === "" ? null : Number(v) })} />
        <N label={tr("Altezza ostacolo")} unit="m" value={o.height} testid={`editor-obstacle-height-${o.id}`} onChange={(v) => set(o, { height: v === "" ? null : Number(v) })} />
      </div>
    </div>
  ));
};

// Disposizione moduli: intero tetto oppure campo moduli disegnato
export const LayoutMode = ({ roof, roofs, commit, ed }) => {
  if (!roof) return null;
  const partial = !!roof.fields_mode || moduleFieldsOf(roof).length > 0;
  return (
    <div className="grid grid-cols-2 gap-1.5" data-testid="editor-layout-mode">
      <button data-testid="editor-layout-whole" onClick={() => commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === roof.id ? { ...r, module_area: null, module_fields: [], fields_mode: false, removed: [], hook_pos: {} } : r)) }))}
        className={`h-9 text-sm rounded-md border flex items-center justify-center gap-1.5 ${!partial ? "bg-[#0F52BA] text-white border-[#0F52BA]" : "border-slate-200 text-slate-600"}`}><Square className="w-4 h-4" />{tr("Intero tetto")}</button>
      <button data-testid="editor-layout-field" onClick={() => { ed.clearDraft(); ed.setTool("draw-field"); }}
        className={`h-9 text-sm rounded-md border flex items-center justify-center gap-1.5 ${partial || ed.tool === "draw-field" ? "bg-[#0F52BA] text-white border-[#0F52BA]" : "border-slate-200 text-slate-600"}`}><PenSquare className="w-4 h-4" />{tr("Campo moduli")}</button>
      {roofs.length > 1 && <p className="col-span-2 text-[11px] text-slate-400">{tr("Il campo disegnato viene assegnato al tetto in cui inizi a disegnare.")}</p>}
    </div>
  );
};

export const structSigOf = (p) => JSON.stringify([p.roofs, p.modules, p.engine, p.building_height, p.roof_type]);

export async function runStructure(p, ed) {
  ed.setBusy(true);
  try {
    await api.put(`/projects/${p.id}`, { roofs: p.roofs, modules: p.modules, engine: p.engine || {}, roof_type: p.roof_type, building_height: p.building_height, covering: p.covering });
    const { data } = await api.post(`/engine/calculate/${p.id}`);
    ed.setStruct({ ...data, sig: structSigOf(p) });
  } catch { toast.error(tr("Calcolo strutturale non riuscito")); } finally { ed.setBusy(false); }
}

export const StructurePanel = ({ p, ed, commit }) => {
  const busy = ed.busy;
  const sig = structSigOf(p);
  const run = () => runStructure(p, ed);
  const manual = (p.roofs || []).some((x) => Object.keys(x.hook_pos || {}).length || x.rail_q);
  const reset = () => {
    const roofs = p.roofs.map(({ hook_pos, rail_q, ...x }) => x);
    commit({ roofs });
    runStructure({ ...p, roofs }, ed);
  };
  const r = ed.struct;
  return (
    <div className="space-y-2" data-testid="editor-structure">
      <button data-testid="editor-calc-structure" onClick={run} disabled={busy}
        className="w-full h-10 rounded-md bg-[#0F52BA] text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}{tr("Calcola struttura (motore)")}
      </button>
      {r && (
        <div className="text-xs space-y-1.5" data-testid="editor-structure-result">
          {r.sig !== sig && <div className="text-amber-700 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{tr("Geometria modificata: ricalcolare.")}</div>}
          <div>{tr("Stato:")} <b data-testid="editor-structure-status">{STATUS[r.status] || r.status}</b> · <span className="text-slate-500">{tr("nessuna approvazione strutturale")}</span></div>
          {r.bom?.hooks !== undefined && <div>{tr("Ganci")} {r.bom.hooks} {tr("· binari")} {fmt(r.bom.rails_m, 1)} m</div>}
          {r.bom?.ballast_kg !== undefined && <div>{tr("Zavorra")} {fmt(r.bom.ballast_kg, 0)} {tr("kg (")}{r.bom.ballast_blocks} {tr("blocchi)")}</div>}
          {r.max_utilization != null && <div>{tr("Sfruttamento max")} <b className={r.max_utilization > 1 ? "text-amber-700" : ""}>{Math.round(r.max_utilization * 100)}%</b>{r.max_utilization > 1 && tr(" — configurazione NON verificata (in arancione)")}</div>}
          {r.missing?.length > 0 && <div className="text-amber-800"><CircleSlash className="w-3.5 h-3.5 inline mr-1" />{tr("Dati mancanti:")} {r.missing.map((m) => m.field).join(", ")}</div>}
          <div className="text-slate-400">{tr("Binari = linee scure, ganci = punti, zavorra = n° blocchi per modulo.")}</div>
          {r.drawing?.groups && <div className="text-slate-400">{tr("Trascina un gancio lungo il binario o un binario sul modulo: gli interassi restano entro i limiti del prodotto e la verifica si ripete al rilascio.")}</div>}
          {manual && (
            <button data-testid="editor-reset-hooks" onClick={reset} disabled={busy} className="w-full h-8 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs">
              {tr("Ripristina ganci e binari automatici")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// travetti reali della falda: primo travetto dal bordo sinistro (guardando la falda dalla gronda) + interasse
export const RafterInputs = ({ roof, commit, globalSpacing }) => {
  const r = roof.rafters || {};
  const set = (patch) => commit((prev) => ({ roofs: prev.roofs.map((x) => (x.id === roof.id ? { ...x, rafters: { ...(x.rafters || {}), ...patch } } : x)) }));
  const num = (v) => (v === "" ? null : Number(String(v).replace(",", ".")));
  return (
    <div data-testid="editor-rafters">
      <div className="grid grid-cols-2 gap-2">
        <N label={tr("1° travetto da sx")} unit="m" value={r.offset} placeholder="—" testid="editor-rafter-offset" onChange={(v) => set({ offset: num(v) })} />
        <N label={tr("Interasse")} unit="m" value={r.spacing} placeholder={globalSpacing ? String(globalSpacing) : "—"} testid="editor-rafter-spacing" onChange={(v) => set({ spacing: num(v) })} />
      </div>
      <p className="text-[11px] text-slate-400 mt-1">{tr("Bordo sinistro guardando la falda dalla gronda. Con i travetti misurati i ganci vanno solo sui travetti (linee marroni).")}</p>
    </div>
  );
};
